// frontend/src/pages/VideoTools/VideoTools.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ToolCard from '../../components/ToolCard/ToolCard';
import api from '../../services/api';

const VideoTools = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // We'll store only the data for the "Video tools" category
  const [videoCategoryData, setVideoCategoryData] = useState(null); 
  const [loading, setLoading] = useState(true);
  
  // Get the current language code (e.g., 'en', 'de', 'fr')
  const lang = i18n.language.split('-')[0];

  useEffect(() => {
    const fetchToolsData = async () => {
      try {
        // We still fetch all tools, as the endpoint provides them grouped
        const response = await api.get('/tools/'); 
        
        // **KEY CHANGE**: Instead of storing all categories,
        // we find and store only the "Text tools" data.
        if (response.data['Video tools']) {
          setVideoCategoryData(response.data['Video tools']);
        } else {
          console.warn('"Video tools" category not found in API response.');
          setVideoCategoryData({ tools: [] }); // Set to empty to avoid errors
        }
        
      } catch (error) {
        console.error('Error fetching video tools:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchToolsData();
  }, []); // Empty dependency array means this runs once on mount

  // This function is identical to Home.jsx, handling navigation
  const handleToolClick = (toolSlug) => {
    navigate(`/tools/${toolSlug}`);
  };

  // Loading state display, same as Home.jsx
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading Video Tools...</div>
      </div>
    );
  }

  // If the category or its tools couldn't be loaded
  if (!videoCategoryData || !videoCategoryData.tools || videoCategoryData.tools.length === 0) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white text-xl text-center">
          <p>Could not load video tools.</p>
          <p className="text-sm text-gray-400 mt-2">Please try again later or contact support.</p>
        </div>
      </div>
    );
  }

  // Dynamically get the translated display name for the category title
  const categoryDisplayName = videoCategoryData[`display_name_${lang}`] || videoCategoryData.display_name_en || 'Video Tools';

  return (
    <div className="min-h-screen bg-brand-dark text-gray-200">
      <div className="container mx-auto px-4 py-12 md:py-16">
        
        {/* Page Header */}
        <div className="text-left mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {categoryDisplayName}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            {/* Using the same translation key pattern as Home.jsx */}
            {t('video_tools_desc')}
          </p>
        </div>

        {/* Tools Grid - Simplified from Home.jsx since we don't need to loop through categories */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {videoCategoryData.tools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onToolClick={handleToolClick}
            />
          ))}
        </div>

      </div>
    </div>
  );
};

export default VideoTools;